/**
 * SubsVendorsScreen — workspace-wide subs & vendors address book.
 * canonical: docs/v3_canonical.html — .sub-row / Subs & Vendors nav (lines 218–235, 586)
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import TrustScoreChip from '../../components/trust/TrustScoreChip'
import SubVendorActionsMenu from '../../components/subs/SubVendorActionsMenu'
import { useSubsVendors } from './useSubsVendors'
import SubsInviteModal from './SubsInviteModal'
import type { SubVendorListItem } from '../../api/subs'

function statusChip(status: SubVendorListItem['status']) {
  if (status === 'active') return <span className="chip c-grn">Active</span>
  if (status === 'invited') return <span className="chip c-amb">Invited</span>
  return <span className="chip c-gry">In book</span>
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('') || '?'
}

function matchesSearch(row: SubVendorListItem, q: string): boolean {
  if (!q) return true
  const lq = q.toLowerCase()
  const v = row.vendor
  return (
    (v.company_name ?? '').toLowerCase().includes(lq) ||
    (v.contact_name ?? '').toLowerCase().includes(lq) ||
    (v.email ?? '').toLowerCase().includes(lq) ||
    (v.trade ?? '').toLowerCase().includes(lq)
  )
}

export default function SubsVendorsScreen() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [showInvite, setShowInvite] = useState(false)
  const { data: rows = [], isPending, isError } = useSubsVendors()

  const filtered = useMemo(
    () => rows.filter(r => matchesSearch(r, search.trim())),
    [rows, search],
  )

  function openTrust(row: SubVendorListItem) {
    if (!row.vendorUserId) return
    navigate(`/subs/${row.vendorUserId}/trust`)
  }

  return (
    <>
      <div className="topbar">
        <div className="crumb"><b>Subs &amp; Vendors</b></div>
        <div className="topbar-r">
          <button
            type="button"
            className="btn b-dk btn-sm"
            onClick={() => setShowInvite(true)}
            data-testid="subs-invite-btn"
          >
            + Invite sub
          </button>
        </div>
      </div>

      <div className="pc">
        <div className="hub-toolbar" style={{ marginBottom: 14 }}>
          <input
            className="hub-search"
            placeholder="Search name, company, email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="subs-search"
          />
          <div style={{ fontSize: 11, color: 'var(--ink4)', whiteSpace: 'nowrap' }}>
            {filtered.length} sub{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="panel" data-testid="subs-address-book-panel">
          <div className="panel-h">
            <div className="panel-tt">
              Address book
              <span className="panel-st">{rows.length} total</span>
            </div>
          </div>

          {isPending && (
            <div style={{ padding: '20px 16px', fontSize: 12, color: 'var(--ink4)' }}>
              Loading subs…
            </div>
          )}

          {isError && (
            <div style={{ padding: '20px 16px', fontSize: 12, color: 'var(--red)' }}>
              Failed to load subs. Try refreshing.
            </div>
          )}

          {!isPending && !isError && filtered.length === 0 && (
            <div className="empty" style={{ padding: '32px 20px' }}>
              <div className="empty-ic">◐</div>
              <div className="empty-tt">
                {rows.length === 0 ? 'No subs in your address book yet' : 'No matches'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink4)', marginBottom: 16, maxWidth: 360, margin: '8px auto 16px' }}>
                Invite subcontractors from a project or add them here to track trust scores across jobs.
              </div>
              {rows.length === 0 && (
                <button className="btn b-dk" type="button" onClick={() => setShowInvite(true)}>
                  + Invite sub
                </button>
              )}
            </div>
          )}

          {!isPending && !isError && filtered.length > 0 && (
            <table className="tbl" data-testid="subs-address-book-table">
              <thead>
                <tr>
                  <th>Sub / vendor</th>
                  <th>Email</th>
                  <th>Trust score</th>
                  <th>Status</th>
                  <th className="r">Projects</th>
                  <th className="r" style={{ width: 56 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => {
                  const clickable = Boolean(row.vendorUserId)
                  return (
                    <tr
                      key={row.vendor.id}
                      style={{ cursor: clickable ? 'pointer' : 'default' }}
                      onClick={() => openTrust(row)}
                      data-testid={`subs-row-${row.vendor.id}`}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            className="sub-av"
                            style={{ background: 'var(--ink2)', width: 28, height: 28, borderRadius: 6 }}
                          >
                            {initials(row.vendor.company_name)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--ink)', fontSize: 12 }}>
                              {row.vendor.company_name}
                            </div>
                            {(row.vendor.contact_name || row.vendor.trade) && (
                              <div style={{ fontSize: 10, color: 'var(--ink4)', marginTop: 2 }}>
                                {[row.vendor.contact_name, row.vendor.trade].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--ink3)' }}>{row.vendor.email}</td>
                      <td>
                        <TrustScoreChip
                          score={row.trustScore}
                          invited={row.status === 'invited' && !row.trustScore}
                        />
                      </td>
                      <td>{statusChip(row.status)}</td>
                      <td className="r mono-c">
                        {row.projectCount > 0 ? row.projectCount : '—'}
                      </td>
                      <td className="r" onClick={e => e.stopPropagation()}>
                        <SubVendorActionsMenu
                          vendorId={row.vendor.id}
                          vendorUserId={row.vendorUserId}
                          companyName={row.vendor.company_name}
                          ownerEmail={row.vendor.email}
                          testId={`subs-actions-${row.vendor.id}`}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showInvite && (
        <SubsInviteModal onClose={() => setShowInvite(false)} />
      )}
    </>
  )
}
